// Identity, as a port. The second implementor is the fake (fake.ts), not a
// second backend — the same justification ContentSource already carries.
//
// The learner's email goes to the auth backend and nowhere else. Not to
// analytics, not to error reporting, and never into a ProgressEvent.

export interface AuthUser {
	id: string;
	email: string;
	/** Ownership of `email` is proven (a code or Google). False for a guest, email attached or not. */
	verified: boolean;
}

/**
 * Appwrite reports no "anonymous" flag. A guest is an account with no email,
 * or with one nobody has proven yet: attaching an email is only half of saving.
 */
export const isGuest = (u: AuthUser): boolean => u.email === "" || !u.verified;

/**
 * The closed set of failures a screen has to have copy for.
 *
 * Closed on purpose: `auth/copy.ts` maps it to UiKeys with a Record, so a new
 * code is a compile error at the one place that must write a sentence for it.
 * An unmapped SDK code lands on `unknown`, which is a real catalog entry —
 * never a runtime fallback, which HANDOFF's rule forbids on either string
 * surface.
 */
export type AuthErrorCode =
	| "emailTaken"
	| "rateLimited"
	| "invalidToken"
	| "network"
	| "forbidden"
	| "transient"
	| "invalidEmail"
	| "accountLocked"
	| "unknown";

/**
 * Carries a code, never the SDK's message: those are English-only and leak
 * internals, and a learner must never see one.
 */
export class AuthError extends Error {
	readonly code: AuthErrorCode;
	/** Appwrite's own error `type`, kept for diagnostics only. Never shown. */
	readonly appwriteType?: string;
	constructor(code: AuthErrorCode, appwriteType?: string) {
		super(code);
		this.name = "AuthError";
		this.code = code;
		this.appwriteType = appwriteType;
	}
}

export interface AuthSource {
	/**
	 * The signed-in user, or null when signed out.
	 *
	 * Null is the ordinary signed-out path (a 401). A network failure THROWS
	 * `AuthError("network")` instead, because the session bootstrap has three outcomes
	 * and collapsing two of them puts a sign-in wall in front of a learner who
	 * is signed in and merely offline.
	 */
	current(): Promise<AuthUser | null>;
	/** A guest: a real, anonymous account whose id carries progress until it is saved. */
	startGuest(): Promise<AuthUser>;
	/**
	 * Email a 6-digit code. Creates the account if the address is new, and
	 * returns the id the code signs into. Not for guests: Appwrite refuses to
	 * attach an email token to an anonymous user.
	 */
	sendCode(email: string): Promise<{ userId: string }>;
	/** An active session — a guest saving — is replaced. */
	verifyCode(userId: string, code: string): Promise<AuthUser>;
	/**
	 * Give the current guest an email, keeping its id, and email it a code.
	 * Appwrite's updateEmail demands a password, so a random throwaway one is
	 * set; it opens nothing, since password sign-in is off.
	 */
	attachEmail(email: string): Promise<{ userId: string }>;
	/**
	 * For an account whose email is proven: remove identities with any other
	 * address and the sessions they opened, and anonymous sessions — what
	 * someone who attached this email to a guest could still hold. Returns the
	 * user as it now stands, or null on failure; never rejects.
	 */
	secureAccount(): Promise<AuthUser | null>;
	signOut(): Promise<void>;
	/**
	 * Erase the account and its rows.
	 *
	 * On the port rather than in a module of its own because it is an identity
	 * operation from the caller's side; the fact that it needs a server-side
	 * Function with an API key is the adapter's problem, and exists because the
	 * event log is append-only: the server denies `delete` on events.
	 */
	deleteAccount(): Promise<void>;
	/**
	 * Initiate an OAuth2 flow for a client-side app.
	 *
	 * Uses `createOAuth2Session` which redirects the browser to the OAuth provider.
	 * On success, Appwrite redirects back to `successUrl` with an active session cookie.
	 * This method does not return on success — the browser navigates away.
	 * On failure, the promise rejects with an AuthError.
	 */
	createOAuth2Session(provider: string, successUrl: string, failureUrl: string): Promise<void>;
}
